import React, { useState, useEffect } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { variables } from "../../config/Variables";
import productService from "../../services/productService";
import { KamaModal } from "../structure/KamaModal";
import IFrameWindow from "../structure/IFrameWindow";
import { ZoomPanImage } from "../structure/ZoomPanImage";
import { 
    IconArrowLeft, 
    Icon3dCubeSphere, 
    IconFileDescription, 
    IconInfoCircle,
    IconTag, 
    IconLayoutGrid, 
    IconEye,
    IconDownload
} from "@tabler/icons-react";

import pdfIcon from "./pdfIcon.png";
import stepIcon from "./stepIcon.png";
import ucdIcon from "./3dIcon.png";
import { formatKatalogKodu, parseOriginalCode } from "../../utils/formatters";

export const ProductDetails = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { t } = useTranslation();

    // State Variables
    const [product, setProduct] = useState(null);
    const [loading, setLoading] = useState(true);
    const [downloadingId, setDownloadingId] = useState(null);
    const [show3dModal, setShow3dModal] = useState(false);
    const [showSpecModal, setShowSpecModal] = useState(false);
    const [activeTab, setActiveTab] = useState("image");

    useEffect(() => {
        const fetchProductDetails = async () => {
            try {
                setLoading(true);
                const data = await productService.getProductById(id);
                if (data) {
                    setProduct(data);
                } else {
                    toast.error(t("mesajUrunBulunamadi"));
                }
            } catch (error) {
                console.error("Error fetching product details", error);
                toast.error(t("mesajIslemBasarisiz"));
            } finally {
                setLoading(false);
            }
        };

        if (id) {
            fetchProductDetails();
        }
    }, [id, t]);

    const handleDownloadPdf = async () => {
        if (!product || !product.PdfFile) return;
        
        const downloadKey = `${product.Id}-pdf`;
        setDownloadingId(downloadKey);
        toast.info(t('mesajPdfIndirmeBasladi'));
        
        try {
            const data = await productService.downloadFile(
                product.PdfFile, 
                sessionStorage.getItem("activationKey"), 
                product.Id, 
                'pdf'
            );
            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', product.PdfFile);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("PDF download failed", error);
            toast.error(t('mesajDosyaIndirmeHatasi'));
        } finally {
            setDownloadingId(null);
        }
    };

    const handleDownloadStep = async () => {
        if (!product || !product.StepFile) return;
        
        const downloadKey = `${product.Id}-step`;
        setDownloadingId(downloadKey);
        toast.info(t('mesajStepIndirmeBasladi'));

        try {
            const data = await productService.downloadFile(
                product.StepFile, 
                sessionStorage.getItem("activationKey"), 
                product.Id, 
                'step'
            );
            const url = window.URL.createObjectURL(new Blob([data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', product.StepFile);
            document.body.appendChild(link);
            link.click();
            link.remove();
        } catch (error) {
            console.error("Step download failed", error);
            toast.error(t('mesajDosyaIndirmeHatasi'));
        } finally {
            setDownloadingId(null);
        }
    };

    if (loading) {
        return (
            <div className="d-flex flex-column align-items-center justify-content-center" style={{ minHeight: "60vh" }}>
                <div className="spinner-border text-primary mb-3" role="status" style={{ width: "3rem", height: "3rem" }}>
                    <span className="visually-hidden">Loading...</span>
                </div>
                <h5 className="text-secondary">{t("Urun Detayları Yükleniyor...", "Ürün Detayları Yükleniyor...")}</h5>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="container py-5 text-center">
                <div className="card shadow border-0 p-5 rounded-4 mx-auto" style={{ maxWidth: "500px" }}>
                    <h3 className="text-danger mb-3">{t("UrunBulunamadi", "Ürün Bulunamadı")}</h3>
                    <p className="text-secondary mb-4">{t("UrunBulunamadiAciklama", "İstediğiniz ürün sistemde bulunamadı veya yetkiniz dışındadır.")}</p>
                    <button className="btn btn-primary fw-bold" onClick={() => navigate("/productlist", { state: location.state })}>
                        <IconArrowLeft size={18} className="me-2" />
                        {t("GeriDon", "Geri Dön")}
                    </button>
                </div>
            </div>
        );
    }

    const details = product.ParsedDetails?.Detaylar;
    const components = product.ParsedDetails?.Bilesenler;
    const categoryTr = product.ParsedDetails?.Kategori || product.Kategori;
    const { code: cleanOriginalCode, description: originalDescription } = parseOriginalCode(product.OrjinalKodu);

    // Filter out null or empty parsed fields for the details table
    const detailRows = [
        { label: t("DetayMarka", "Marka"), val: details?.Marka, code: components?.Marka },
        { label: t("DetayOnKod", "Ön Kod"), val: details?.OnKod, code: components?.OnKod },
        { label: t("DetaySeri", "Seri"), val: details?.Seri, code: components?.Seri },
        { label: t("DetayBaglanti", "Bağlantı Tipi"), val: details?.Baglanti, code: components?.Baglanti },
        { label: t("DetayGovde", "Gövde Cinsi"), val: details?.Govde, code: components?.Govde },
        { label: t("DetaySirtTipi", "Sırt Tipi"), val: details?.SirtTipi, code: components?.SirtTipi },
        { label: t("DetaySirtMalzeme", "Sırt Malzemesi"), val: details?.SirtMalzemesi, code: components?.SirtMalzemesi },
        { label: t("DetayYatak", "Yatak Tipi"), val: details?.Yatak, code: components?.Yatak },
        { label: t("DetayCap", "Tekerlek Çapı"), val: details?.Cap, code: components?.Cap },
        { label: t("DetayGenislik", "Tekerlek Genişliği"), val: details?.Genislik, code: components?.Genislik },
        { label: t("DetayFren", "Fren Tipi"), val: details?.Fren, code: components?.Fren },
        { label: t("DetayYay", "Yay Kodu"), val: details?.Yay, code: components?.Yay },
        { label: t("DetayRenk", "Renk"), val: details?.Renk, code: components?.Renk },
    ].filter(row => row.val);

    return (
        <div className="container-fluid py-4" style={{ maxWidth: "1280px" }}>
            {/* Header / Back Action */}
            <div className="d-flex align-items-center justify-content-between mb-4">
                <button 
                    className="btn btn-light bg-white border shadow-sm rounded-3 px-3 py-2 fw-bold d-flex align-items-center gap-2"
                    style={{ color: "#475569", transition: "all 0.2s" }}
                    onClick={() => navigate("/productlist", { state: location.state })}
                >
                    <IconArrowLeft size={18} />
                    {t("GeriDon", "Ürün Listesine Dön")}
                </button>
                <span 
                    className="fw-bold" 
                    style={{ 
                        background: '#eff6ff', 
                        color: '#2563a8', 
                        fontSize: '13px', 
                        padding: '6px 16px', 
                        borderRadius: '20px', 
                        userSelect: 'none',
                        border: '1px solid #bfdbfe'
                    }}
                >
                    {categoryTr}
                </span>
            </div>

            {/* Main Content: Two Columns */}
            <div className="row g-4">
                {/* Left Column: Parsed Specs Card */}
                <div className="col-12 col-lg-7">
                    <div className="card border-0 shadow-sm rounded-4 p-4 mb-4">
                        {/* Combined Summary Card (Ürün Kodu & Ürün Açıklaması) */}
                        <div className="card border-0 rounded-4 mb-3" style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                            <div className="card-body p-3.5">
                                <div className="row g-3 align-items-start">
                                    {/* Sol Kolon: Ürün Kodu ve Katalog Kodu */}
                                    <div className="col-12 col-md-5 d-flex flex-column align-items-start pe-md-3" style={{ borderRight: "1px solid #e2e8f0" }}>
                                        <div className="d-flex align-items-center gap-2 flex-wrap mb-2">
                                            <h2 className="h4 fw-bold text-slate-800 m-0" style={{ color: "#1e293b" }}>
                                                {cleanOriginalCode}
                                            </h2>
                                            {originalDescription && (
                                                <span 
                                                    className="badge fw-medium px-2 py-0.5 rounded" 
                                                    style={{ 
                                                        backgroundColor: "#ffffff", 
                                                        color: "#475569", 
                                                        border: "1px solid #cbd5e1",
                                                        fontSize: "12px"
                                                    }}
                                                >
                                                    {originalDescription}
                                                </span>
                                            )}
                                        </div>
                                        {product.KatalogKodu && (
                                            <div className="d-flex align-items-center gap-2 mt-1">
                                                <span className="badge" style={{ backgroundColor: "#1e293b", color: "#ffffff", fontSize: "16px", fontWeight: 700, padding: "6px 12px", borderRadius: "8px", letterSpacing: "0.2px" }}>
                                                    {formatKatalogKodu(product.KatalogKodu)}
                                                </span>
                                                <button 
                                                    className="btn btn-sm d-flex align-items-center justify-content-center"
                                                    style={{ 
                                                        backgroundColor: "#ffffff", 
                                                        color: "#1e293b", 
                                                        border: "1px solid #cbd5e1",
                                                        borderRadius: "50%",
                                                        width: "26px",
                                                        height: "26px",
                                                        padding: "0",
                                                        fontSize: "13px",
                                                        fontWeight: "bold",
                                                        cursor: "pointer",
                                                        transition: "all 0.15s"
                                                    }}
                                                    onMouseEnter={e => {
                                                        e.currentTarget.style.backgroundColor = "#2563eb";
                                                        e.currentTarget.style.borderColor = "#2563eb";
                                                        e.currentTarget.style.color = "#ffffff";
                                                    }}
                                                    onMouseLeave={e => {
                                                        e.currentTarget.style.backgroundColor = "#ffffff";
                                                        e.currentTarget.style.borderColor = "#cbd5e1";
                                                        e.currentTarget.style.color = "#1e293b";
                                                    }}
                                                    onClick={() => {
                                                        setShowSpecModal(true);
                                                        productService.logKodAciklamasi(product.KatalogKodu, product.Id);
                                                    }}
                                                    title={t("KodAciklamasi", "KOD AÇIKLAMASI")}
                                                >
                                                    ?
                                                </button>
                                            </div>
                                        )}
                                    </div>

                                    {/* Sağ Kolon: Ürün Açıklaması */}
                                    <div className="col-12 col-md-7 ps-md-3">
                                        <div className="d-flex align-items-center gap-1.5 mb-1.5" style={{ color: '#94a3b8', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                            <IconFileDescription size={14} />
                                            {t("TamUrunAciklamasi", "Ürün Açıklaması")}
                                        </div>
                                        <p className="m-0 text-slate-700 fw-medium" style={{ color: "#334155", fontSize: "14px", lineHeight: "1.5" }}>
                                            {product.ParsedDetails?.TamAciklama || formatKatalogKodu(product.KatalogKodu)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Teknik Çizim Özellikleri Tablosu */}
                        {((product.TeknikDetaylar && product.TeknikDetaylar.length > 0) || 
                          (product.teknikDetaylar && product.teknikDetaylar.length > 0)) && (
                            <div className="mt-2">
                                <div className="d-flex align-items-center gap-2 mb-2.5"
                                    style={{ color: '#94a3b8', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                    <IconFileDescription size={14} />
                                    {t("TeknikResimOzellikleri", "Teknik Çizim Ölçü Verileri")}
                                </div>
                                <div className="table-responsive rounded-3 border">
                                    <table className="table table-striped table-hover mb-0 align-middle">
                                        <thead>
                                            <tr style={{ background: "#f8fafc" }}>
                                                <th className="ps-4 py-2 text-secondary font-semibold" style={{ width: "40%", fontSize: "13px", textTransform: "uppercase" }}>{t("Ozellik", "Özellik")}</th>
                                                <th className="pe-4 py-2 text-secondary font-semibold" style={{ fontSize: "13px", textTransform: "uppercase" }}>{t("Deger", "Değer")}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {(product.TeknikDetaylar || product.teknikDetaylar).map((spec, index) => {
                                                const label = spec.NitelikAdi || spec.nitelikAdi;
                                                const value = spec.Deger || spec.deger;
                                                return (
                                                    <tr key={index}>
                                                        <td className="ps-4 py-2 fw-semibold text-slate-700" style={{ color: "#475569", fontSize: "14px", lineHeight: "1.2" }}>
                                                            {label}
                                                        </td>
                                                        <td className="pe-4 py-2 text-slate-900" style={{ color: "#0f172a", fontSize: "14px", lineHeight: "1.2" }}>
                                                            {value}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                </div>

                {/* Right Column: Visual Component (Image & Schema Tabbed) */}
                <div className="col-12 col-lg-5">
                    <div className="card border-0 shadow-lg rounded-4 overflow-hidden" style={{ position: "sticky", top: "24px" }}>
                        
                        {/* Tab Headers */}
                        <div className="d-flex bg-light border-bottom">
                            <button 
                                className={`btn flex-fill py-3 rounded-0 border-0 fw-bold d-flex align-items-center justify-content-center gap-2`}
                                style={{
                                    fontSize: "14px",
                                    color: activeTab === "image" ? "#2563eb" : "#64748b",
                                    background: activeTab === "image" ? "#ffffff" : "transparent",
                                    borderBottom: activeTab === "image" ? "3px solid #2563eb" : "none",
                                    transition: "all 0.15s"
                                }}
                                onClick={() => setActiveTab("image")}
                            >
                                <IconEye size={18} />
                                {t("UrunGorseli", "Ürün Görseli")}
                            </button>
                            {product.SchemaFile && (
                                <button 
                                    className={`btn flex-fill py-3 rounded-0 border-0 fw-bold d-flex align-items-center justify-content-center gap-2`}
                                    style={{
                                        fontSize: "14px",
                                        color: activeTab === "schema" ? "#ea580c" : "#64748b",
                                        background: activeTab === "schema" ? "#ffffff" : "transparent",
                                        borderBottom: activeTab === "schema" ? "3px solid #ea580c" : "none",
                                        transition: "all 0.15s"
                                    }}
                                    onClick={() => setActiveTab("schema")}
                                >
                                    <IconFileDescription size={18} />
                                    {t("TeknikSema", "Teknik Şema")}
                                </button>
                            )}
                        </div>

                        {/* Image Body */}
                        <div className="card-body p-4 text-center d-flex align-items-center justify-content-center" style={{ minHeight: "360px", background: "#ffffff" }}>
                            {activeTab === "image" ? (
                                <img 
                                    src={variables.IMAGE_URL + product.ImageFile} 
                                    alt={product.OrjinalKodu} 
                                    className="img-fluid"
                                    style={{ maxHeight: "320px", objectFit: "contain", transition: "transform 0.2s" }}
                                    onMouseEnter={e => e.currentTarget.style.transform = "scale(1.04)"}
                                    onMouseLeave={e => e.currentTarget.style.transform = "scale(1)"}
                                />
                            ) : (
                                <ZoomPanImage 
                                    src={variables.SCHEMA_URL + product.SchemaFile} 
                                    alt="Technical Schema" 
                                    className="img-fluid"
                                />
                            )}
                        </div>

                        {/* Downloads / Actions panel at the bottom of Right Card */}
                        <div className="card-footer bg-light p-4 border-top">
                            <div className="d-flex flex-column gap-3">
                                
                                <div className="row g-2">
                                    {/* PDF Download Button */}
                                    {product.PdfFile && (
                                        <div className="col-6">
                                            <button 
                                                className="btn btn-danger w-100 py-2.5 fw-bold d-flex align-items-center justify-content-center gap-2"
                                                onClick={handleDownloadPdf}
                                                disabled={downloadingId !== null}
                                                style={{ borderRadius: "10px" }}
                                            >
                                                {downloadingId === `${product.Id}-pdf` ? (
                                                    <div className="spinner-border spinner-border-sm text-white" role="status" />
                                                ) : (
                                                    <>
                                                        <img width={20} src={pdfIcon} alt="PDF" />
                                                        <span>{t("PdfIndir", "PDF")}</span>
                                                        <IconDownload size={16} />
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}

                                    {/* STEP Download Button */}
                                    {product.StepFile && (
                                        <div className="col-6">
                                            <button 
                                                className="btn btn-primary w-100 py-2.5 fw-bold d-flex align-items-center justify-content-center gap-2"
                                                onClick={handleDownloadStep}
                                                disabled={downloadingId !== null}
                                                style={{ 
                                                    borderRadius: "10px", 
                                                    backgroundColor: "#2563eb", 
                                                    borderColor: "#2563eb",
                                                    transition: "all 0.15s ease-in-out" 
                                                }}
                                                onMouseEnter={e => {
                                                    e.currentTarget.style.backgroundColor = "#1d4ed8";
                                                    e.currentTarget.style.borderColor = "#1d4ed8";
                                                }}
                                                onMouseLeave={e => {
                                                    e.currentTarget.style.backgroundColor = "#2563eb";
                                                    e.currentTarget.style.borderColor = "#2563eb";
                                                }}
                                            >
                                                {downloadingId === `${product.Id}-step` ? (
                                                    <div className="spinner-border spinner-border-sm text-white" role="status" />
                                                ) : (
                                                    <>
                                                        <img width={20} src={stepIcon} alt="STEP" />
                                                        <span>{t("StepIndir", "STEP")}</span>
                                                        <IconDownload size={16} />
                                                    </>
                                                )}
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* 3D View Button */}
                                {product.ModelFile && (
                                    <button 
                                        className="btn btn-success w-100 py-3 fw-bold d-flex align-items-center justify-content-center gap-2 text-white shadow-sm"
                                        onClick={() => setShow3dModal(true)}
                                        style={{ borderRadius: "10px", background: "linear-gradient(135deg, #10b981 0%, #059669 100%)", border: "none" }}
                                    >
                                        <img width={24} src={ucdIcon} alt="3D" />
                                        <span style={{ fontSize: "15px" }}>{t("3dModelGorunumu", "3D CAD Modelini Görün")}</span>
                                    </button>
                                )}

                            </div>
                        </div>

                    </div>
                </div>

            </div>

            {/* 3D View Modal */}
            <KamaModal
                show={show3dModal}
                onClose={() => setShow3dModal(false)}
                title={<>{t('menu3dView', '3D Görünüm')}<span style={{ opacity: 0.85, fontWeight: 500, fontSize: '15px' }}> — {cleanOriginalCode}</span></>}
                icon={<Icon3dCubeSphere size={20} />}
                headerGradient="linear-gradient(135deg, #1e2a38 0%, #059669 100%)"
                size="fullscreen"
                footer={<button className="btn btn-outline-secondary fw-bold px-5" onClick={() => setShow3dModal(false)}>{t('butonKapat', 'Kapat')}</button>}
                footerStyle={{ background: '#1e2a38', borderTop: '1px solid rgba(255,255,255,0.1)' }}
            >
                <IFrameWindow productUrl={product.ModelFile} />
            </KamaModal>

            {/* Product Specs Modal */}
            <KamaModal
                show={showSpecModal}
                onClose={() => setShowSpecModal(false)}
                title={t('KamaRuletKodAciklamasi', 'Kama Rulet Kod Açıklaması')}
                icon={<IconLayoutGrid size={20} />}
                headerGradient="linear-gradient(135deg, #1e2a38 0%, #2563eb 100%)"
                size="lg"
            >
                <div className="px-2 py-1">
                    {/* Combined Summary Card (Ürün Kodu & Ürün Açıklaması) */}
                    {product && (
                        <div className="card border-0 rounded-4 shadow-sm mb-4" style={{ backgroundColor: "#f8fafc", border: "1px solid #e2e8f0" }}>
                            <div className="card-body p-4">
                                <div className="row g-4 align-items-start">
                                    {/* Sol Kolon: Ürün Kodu */}
                                    <div className="col-12 col-md-5 d-flex flex-column align-items-start pe-md-4" style={{ borderRight: "1px solid #e2e8f0" }}>
                                        <div className="d-flex align-items-center gap-1.5 mb-2" style={{ color: '#94a3b8', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                            <IconTag size={14} />
                                            {t("KatalogKoduBaslik", "KATALOG KODU")}
                                        </div>
                                        <span className="badge" style={{ backgroundColor: "#1e293b", color: "#ffffff", fontSize: "18px", fontWeight: 700, padding: "7px 14px", borderRadius: "8px", letterSpacing: "0.2px" }}>
                                            {formatKatalogKodu(product.KatalogKodu)}
                                        </span>
                                    </div>

                                    {/* Sağ Kolon: Ürün Açıklaması */}
                                    <div className="col-12 col-md-7 ps-md-4">
                                        <div className="d-flex align-items-center gap-1.5 mb-2" style={{ color: '#94a3b8', fontSize: '11px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px' }}>
                                            <IconFileDescription size={14} />
                                            {t("TamUrunAciklamasi", "Ürün Açıklaması")}
                                        </div>
                                        <p className="m-0 text-slate-700 fw-medium" style={{ color: "#334155", fontSize: "14px", lineHeight: "1.6" }}>
                                            {product.ParsedDetails?.TamAciklama || formatKatalogKodu(product.KatalogKodu)}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Özellikler Tablosu */}
                    {detailRows.length > 0 ? (
                        <div className="table-responsive rounded-4 border overflow-hidden shadow-sm mb-4" style={{ borderColor: "#e2e8f0" }}>
                            <table className="table table-striped table-hover mb-0 align-middle">
                                <thead>
                                    <tr style={{ background: "#f8fafc" }}>
                                        <th className="ps-4 py-3 text-secondary font-semibold" style={{ width: "35%", fontSize: "11px", textTransform: "uppercase" }}>{t("Ozellik", "Özellik")}</th>
                                        <th className="py-3 text-secondary font-semibold text-center" style={{ width: "20%", fontSize: "11px", textTransform: "uppercase" }}>{t("KodKarakteri", "Kod Karakteri")}</th>
                                        <th className="pe-4 py-3 text-secondary font-semibold" style={{ fontSize: "11px", textTransform: "uppercase" }}>{t("Aciklama", "Açıklama")}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {detailRows.map((row, idx) => (
                                        <tr key={idx}>
                                            <td className="ps-4 py-2.5 fw-semibold text-slate-700" style={{ color: "#475569", fontSize: "13px" }}>
                                                {row.label}
                                            </td>
                                            <td className="py-2.5 text-center">
                                                <span className="badge px-2 py-0.5 rounded" style={{ backgroundColor: "#f1f5f9", color: "#0f172a", fontSize: "12px", fontWeight: "bold", border: "1px solid #cbd5e1" }}>
                                                    {row.code || "-"}
                                                </span>
                                            </td>
                                            <td className="pe-4 py-2.5 text-slate-900 fw-medium" style={{ color: "#0f172a", fontSize: "13px" }}>
                                                {row.val}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="p-4 text-center text-muted rounded-4 bg-light mb-4">
                            <IconInfoCircle size={24} className="mb-2" />
                            <p className="m-0">{t("DetayBulunamadi", "Ürün özellik detay bilgisi bulunamadı.")}</p>
                        </div>
                    )}

                    {/* Additional Information (badges) */}
                    {details?.Ilave && details.Ilave.length > 0 && (
                        <div className="p-4 rounded-4 bg-light border mb-4" style={{ borderColor: "#e2e8f0" }}>
                            <h6 className="fw-bold text-slate-700 mb-3 d-flex align-items-center gap-2" style={{ color: "#334155" }}>
                                <IconTag size={18} />
                                {t("IlaveBilgiler", "İlave Bilgiler ve Ek Özellikler")}
                            </h6>
                            <div className="d-flex flex-wrap gap-2">
                                {details.Ilave.map((item, idx) => (
                                    <span key={idx} className="badge bg-primary text-white rounded-pill px-3 py-2 fw-semibold" style={{ fontSize: "12px", letterSpacing: "0.2px" }}>
                                        {item}
                                    </span>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Modal Footer Row */}
                    <div className="d-flex justify-content-end align-items-center pt-3 pb-2 mt-2">
                        <button
                            type="button"
                            className="btn btn-secondary px-4 py-2 fw-semibold rounded-3 shadow-sm"
                            onClick={() => setShowSpecModal(false)}
                        >
                            {t("Kapat", "Kapat")}
                        </button>
                    </div>
                </div>
            </KamaModal>
        </div>
    );
};
